import React, { useState } from 'react';
import cx from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import DashboardCard from './DashboardCard';
import RecentReservation from './RecentReservation';
import RevenuePerformanceChart from './RevenuePerformanceChart';
import PlatformEarnings from './PlatformEarnings';
import DateFilter from './DateFilter';
import messages from '../../../locale/jsonMessages';
import { dateFormat } from '../../../config';
import { BasKetSizeIcon, CalendarIcon, CancellationRateIcon, DashBoardIcon, ReservationIcon } from '../../Common/Assets/SvgIconComponent/CommonSvgIcon';
import { SideMenuManageCarsIcon, SideMenuManageServiceFeeIcon } from '../../Common/Assets/SvgIconComponent/SideMenuRemainingIcons';
import cs from '../../../components/commonStyle.css';
import s from './AdminDashboard.css';
import { useSelector } from 'react-redux';
import ToolTipSection from '../../Common/ToolTipSection/ToolTipSection';
import { formatLocalizedPaymentDate } from '../../../helpers/formatLocateDate';


const AdminDashboard = (props) => {

    const [isCustom, setIsCustom] = useState(false);
    const [dateStart, setDateStart] = useState("");
    const [dateEnd, setDateEnd] = useState("");
    const [filterOption, setFilterOption] = useState("");
    const { formatMessage, locale } = useIntl();

    const {
        user: { getUserDashboard },
        listing: { getListingDashboard },
        reservation: { getReservationDashboard },
        adminUserData,
        onDateFilterChange
    } = props;

    let dates;

    const validPrivilege = adminUserData?.getAdminUser?.adminRole?.privileges;
    const isAdminUser = adminUserData?.getAdminUser?.isSuperAdmin;
    const toCurrency = useSelector(state => state.currency.base);

    const getDisplayTotalForFilter = (item) => {
        const opt = filterOption || 'last30Days';
        if (opt === 'custom') return item.customCount ?? 0;
        if (opt === 'last7Days') return item.last7DaysCount;
        if (opt === 'last24Hours') return item.last24HoursCount;
        return item.last30DaysCount;
    };
    const dashboardData = [
        {
            key: 'users',
            title: <FormattedMessage  {...messages.totalUsers} />,
            last24HoursCount: getUserDashboard?.todayCount || 0,
            last7DaysCount: getUserDashboard?.weekCount || 0,
            last30DaysCount: getUserDashboard?.monthCount || 0,
            total: getUserDashboard?.totalCount || 0,
            type: 'users',
            management: <FormattedMessage  {...messages.manageUser} />,
            customCount: getUserDashboard?.customCount,
            icon: DashBoardIcon,
            iconColor: '#FF5757',
            iconBgColor: '#FFEDED',
            privilegeId: 2,
            isAdminUser

        },
        {
            key: 'listings',
            title: <FormattedMessage  {...messages.totalCars} />,
            last24HoursCount: getListingDashboard?.todayCount || 0,
            last7DaysCount: getListingDashboard?.weekCount || 0,
            last30DaysCount: getListingDashboard?.monthCount || 0,
            total: getListingDashboard?.totalCount || 0,
            type: 'listings',
            management: <FormattedMessage  {...messages.manageListing} />,
            customCount: getListingDashboard?.customCount,
            icon: SideMenuManageCarsIcon,
            iconColor: '#0096c7',
            iconBgColor: '#d6f8ff',
            privilegeId: 3,
            isAdminUser
        },
        {
            key: 'reservations',
            title: <FormattedMessage  {...messages.totalReservations} />,
            last24HoursCount: getReservationDashboard?.todayCount || 0,
            last7DaysCount: getReservationDashboard?.weekCount || 0,
            last30DaysCount: getReservationDashboard?.monthCount || 0,
            total: getReservationDashboard?.totalCount || 0,
            type: 'reservations',
            management: <FormattedMessage  {...messages.manageReservations} />,
            customCount: getReservationDashboard?.customCount,
            icon: ReservationIcon,
            iconColor: '#4E47DD',
            iconBgColor: '#F0F4FF',
            privilegeId: 4,
            isAdminUser
        },
        {
            key: 'earnings',
            title: <FormattedMessage  {...messages.earnings} />,
            last24HoursCount: getReservationDashboard?.todayAdminEarnings || 0,
            last7DaysCount: getReservationDashboard?.weeklyAdminEarnings || 0,
            last30DaysCount: getReservationDashboard?.monthlyAdminEarnings || 0,
            total: getReservationDashboard?.totalAdminEarnings || 0,
            type: 'earnings',
            customCount: getReservationDashboard?.customAdminEarnings,
            icon: SideMenuManageServiceFeeIcon,
            iconColor: '#19AF7D',
            iconBgColor: '#F0FCF8',
            isEarnings: true,
            currency: toCurrency
        },
        {
            key: 'averageBookingAmount',
            title: <FormattedMessage {...messages.averageBookingAmount} />,
            total: getReservationDashboard?.averageBookingAmount || 0,
            currency: toCurrency,
            icon: BasKetSizeIcon,
            iconColor: '#fb9645',
            iconBgColor: '#fff8ed',
            isAverageBooking: true,
        },
        {
            key: 'overallCancellationRate',
            title: <FormattedMessage {...messages.overallCancellationRate} />,
            total: getReservationDashboard?.percentageCancellation || 0,
            icon: CancellationRateIcon,
            iconColor: '#ef4a4b',
            iconBgColor: '#fef2f2',
            isCancellationRate: true,
        }
    ];

    const handleDateFilterChange = (startDate, endDate, filterOption) => {
        if (onDateFilterChange) {
            setDateStart(startDate);
            setDateEnd(endDate);
            setIsCustom(filterOption === 'custom');
            setFilterOption(filterOption);
            onDateFilterChange(startDate, endDate, filterOption);
        }
    };

    if (dateStart && dateEnd) {
        dates = `${formatLocalizedPaymentDate(dateStart, locale, dateFormat?.mediumDateFormat)} - ${formatLocalizedPaymentDate(dateEnd, locale, dateFormat?.mediumDateFormat)}`;
    }
    let showManagementLink = isAdminUser ? isAdminUser : validPrivilege?.includes(4);
    return (
        <div>
            <div className={cx(s.dashboardHeaderFlexWrapper, cs.spaceBottom4)}>
                <h1 className={'siteAdminHeading'} style={{ marginBottom: 0 }}><FormattedMessage  {...messages.dashboard} /></h1>
                <DateFilter onDateChange={handleDateFilterChange} setIsCustom={setIsCustom} dates={dates} dateStart={dateStart} dateEnd={dateEnd} />
            </div>
            <div className={s.dashboardCardGridWrapper}>
                {dashboardData.map((item) => (
                    <DashboardCard
                        key={item.key}
                        title={item.title}
                        last24HoursCount={item.last24HoursCount}
                        last30DaysCount={item.last30DaysCount}
                        last7DaysCount={item.last7DaysCount}
                        total={item.total}
                        displayTotal={getDisplayTotalForFilter(item)}
                        icon={item.icon}
                        type={item.type}
                        management={item.management}
                        showManagementLink={item.showManagementLink}
                        customCount={item.customCount}
                        isCustom={isCustom}
                        dates={dates}
                        iconColor={item?.iconColor}
                        iconBgColor={item?.iconBgColor}
                        privilegeId={item?.privilegeId}
                        validPrivilege={validPrivilege}
                        isAdminUser={item?.isAdminUser}
                        isEarnings={item?.isEarnings}
                        isAverageBooking={item?.isAverageBooking}
                        isCancellationRate={item?.isCancellationRate}
                        currency={item?.currency}
                    />
                ))}
            </div>
            {(dateStart && dateEnd) && <div className={cx(s.dateRangeBatch, cs.spaceBottom4, cs.spaceTop4)}> <CalendarIcon /><span>{dates}</span>  {filterOption === 'last24Hours' && <ToolTipSection label={formatMessage(messages.hoursFilterToolTip)} tooltipIconClass={s.dateTooltipIcon} />}</div>}
            <div className={cx(s.dashboardRevenueAndPlatformEarningsGridWrapper, cs.spaceBottom4)}>
                <RevenuePerformanceChart
                    totalAmount={getReservationDashboard?.totalAmount}
                    totalGuestServiceFee={getReservationDashboard?.totalGuestServiceFee}
                    totalHostServiceFee={getReservationDashboard?.totalHostServiceFee}
                    monthlyTotals={getReservationDashboard?.monthlyTotals}
                    dateStart={dateStart}
                    dateEnd={dateEnd}
                    filterOption={filterOption}
                    baseCurrencySymbol={getReservationDashboard?.baseCurrencySymbol}
                />
                <PlatformEarnings
                    totalGuestServiceFee={getReservationDashboard?.totalGuestServiceFee}
                    totalHostServiceFee={getReservationDashboard?.totalHostServiceFee}
                    baseCurrencySymbol={getReservationDashboard?.baseCurrencySymbol}
                    monthlyTotals={getReservationDashboard?.monthlyTotals}
                    dateStart={dateStart}
                    dateEnd={dateEnd}
                    totalAmount={getReservationDashboard?.totalAmount}
                    dates={dates}
                />
            </div>
            <RecentReservation isAdminUser={showManagementLink} />
        </div>
    );
};


AdminDashboard.propTypes = {
    user: PropTypes.object,
    listing: PropTypes.object,
    reservation: PropTypes.object,
    onDateFilterChange: PropTypes.func,
    isSuperAdmin: PropTypes.bool
};

export default withStyles(cs, s)(AdminDashboard);
